import { useState, useEffect, useRef, type ChangeEventHandler } from 'react';
import ReactDOM from 'react-dom';
import { Eye, EyeOff, Upload, CheckCircle, X, Video } from 'lucide-react';
import { getRegister, checkPhoneApi } from './yogatrainerCredentials';
import toast from 'react-hot-toast';
import { GetCountries, GetState, GetCity } from "react-country-state-city";
import ReactSelect from "react-select";
import { sendOtp, verifyOtp } from '../instrutorProfile/profileAPIs';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface OTPResponse {
  success: boolean;
  message: string;
  verified?: boolean;
  attemptsRemaining?: number;
}

interface SignupCategory {
  _id: string;
  name: string;
  slug?: string;
  priority?: number;
  isActive?: boolean;
}

interface SignupCategoryResponse {
  categories?: SignupCategory[];
}

const RegisterModal = ({ isOpen, onClose }: RegisterModalProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpError, setOtpError] = useState<string>('');
  const [phoneNumberForOTP, setPhoneNumberForOTP] = useState<string>('');
  const [bankValidationErrors, setBankValidationErrors] = useState<{
    bankName?: string;
    accountNumber?: string;
    ifscCode?: string;
  }>({});
  const [selectedCountryId, setSelectedCountryId] = useState<number>(0);
  const [selectedStateId, setSelectedStateId] = useState<number>(0);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    subscribeNewsletter: false,
    role: ['Regular Classes'] as string[],
    bankName: '',
    accountNumber: '',
    ifscCode: '',
  });

  const [countries, setCountries] = useState<{ value: number, label: string, name: string }[]>([]);
  const [states, setStates] = useState<{ value: number, label: string, name: string }[]>([]);
  const [cities, setCities] = useState<{ value: number, label: string, name: string }[]>([]);

  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  const vendorLoginPanelUrl =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3000/'
      : 'https://partner.metafitwellness.com/';

  // Timer for OTP resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    GetCountries().then((data: any) => {
      setCountries(data.map((c: any) => ({ value: c.id, label: c.name, name: c.name })));
      setIsLoadingCountries(false);
    });
  }, []);

  useEffect(() => {
    if (selectedCountryId) {
      setIsLoadingStates(true);
      setStates([]);
      GetState(selectedCountryId).then((data: any) => {
        setStates(data.map((s: any) => ({ value: s.id, label: s.name, name: s.name })));
        setIsLoadingStates(false);
      });
    } else {
      setStates([]);
    }
  }, [selectedCountryId]);

  useEffect(() => {
    if (selectedCountryId && selectedStateId) {
      setIsLoadingCities(true);
      setCities([]);
      GetCity(selectedCountryId, selectedStateId).then((data: any) => {
        setCities(data.map((c: any) => ({ value: c.id, label: c.name, name: c.name })));
        setIsLoadingCities(false);
      });
    } else {
      setCities([]);
    }
  }, [selectedCountryId, selectedStateId]);

  const handleBackFromStep2 = () => {
    if (otpVerified) {
      setCurrentStep(3);
    } else {
      setCurrentStep(1);
      setOtpError('');
    }
  };

  const validateAccountNumber = (value: string): boolean => {
    const cleaned = value.replace(/\s/g, '');
    return /^\d{9,18}$/.test(cleaned);
  };

  const validateIFSCCode = (value: string): boolean => {
    return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase().trim());
  };

  const validateBankName = (value: string): boolean => {
    const trimmed = value.trim();
    return /^[a-zA-Z\s-]{3,50}$/.test(trimmed);
  };

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    let finalValue = value;

    if (name === 'accountNumber') {
      finalValue = value.replace(/[^\d]/g, '').slice(0, 18);
    } else if (name === 'pincode') {
      finalValue = value.replace(/[^\d]/g, '').slice(0, 6);
    } else if (name === 'ifscCode') {
      finalValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 11);
    } else if (name === 'bankName') {
      finalValue = value.replace(/[^a-zA-Z\s-]/g, '').slice(0, 50);
    } else if (name === 'city' || name === 'state' || name === 'country') {
      finalValue = value.replace(/[^a-zA-Z\s-]/g, '').slice(0, 50);
    } else if (name === 'address') {
      finalValue = value.slice(0, 180);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : finalValue
    }));

    if (name === 'phone') {
      const newPhone = finalValue.replace(/\D/g, '');
      if (newPhone !== phoneNumberForOTP) {
        setOtpError('');
        setOtpSent(false);
        setOtpVerified(false);
        setOtp(['', '', '', '', '', '']);
        setPhoneNumberForOTP('');
      }
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const checkPhone = async () => {
      const phoneNumber = formData.phone.replace(/\D/g, '');
      if (phoneNumber.length === 10) {
        try {
          const res = await checkPhoneApi(phoneNumber);
          if (res.success && res.exists) {
            toast.success('Vendor account already exists. Redirecting to login...', {
              duration: 3000,
              icon: '🔒'
            });
            setTimeout(() => {
              const url = `${vendorLoginPanelUrl}?phone=${encodeURIComponent(phoneNumber)}`;
              window.location.assign(url);
            }, 2500);
          }
        } catch (error) {
          console.error('Error checking phone existence:', error);
        }
      }
    };

    checkPhone();
  }, [formData.phone, isOpen]);

  const handleCertChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setCertFile(file);
    }
  };

  // Replaced country, state, city change handlers with inline react-select logic

  const sendOTP = async () => {
    if (!formData.phone) {
      toast.error('Please enter phone number first');
      return;
    }

    const phoneNumber = formData.phone.replace(/\D/g, '');
    if (!/^\d{10}$/.test(phoneNumber)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    if (otpSent && phoneNumber === phoneNumberForOTP && !otpVerified && resendTimer > 0) {
      toast('OTP already sent to this number. Please wait for the timer.');
      return;
    }

    if (otpVerified && phoneNumber === phoneNumberForOTP) {
      return;
    }

    const phoneToSend = phoneNumber;
    setIsSendingOTP(true);
    setOtpError('');

    try {
      const data: OTPResponse = await sendOtp(phoneToSend);

      if (data.success) {
        toast.success(data.message || `OTP sent to ${phoneNumber.slice(0, 2)}****${phoneNumber.slice(-2)}`);
        setOtpSent(true);
        setPhoneNumberForOTP(phoneNumber);
        setResendTimer(60);
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => {
          otpInputRefs.current[0]?.focus();
        }, 100);
      } else {
        toast.error(data.message || 'Failed to send OTP');
        setOtpError(data.message || '');
      }
    } catch (error: any) {
      console.error("[Signup] Network error during OTP send:", error);
      toast.error('Failed to send OTP. Please try again.');
      setOtpError('Network error. Please try again.');
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');

    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    if (newOtp.every(digit => digit !== '')) {
      verifyOTP(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const verifyOTP = async (providedOtp?: string) => {
    const otpString = providedOtp || otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter complete 6-digit OTP');
      return;
    }

    const phoneNumber = formData.phone.replace(/\D/g, '');
    if (!/^\d{10}$/.test(phoneNumber)) {
      toast.error('Invalid phone number');
      return;
    }

    if (phoneNumber !== phoneNumberForOTP) {
      toast.error('Phone number changed. Please send OTP again.');
      setOtpSent(false);
      setOtp(['', '', '', '', '', '']);
      return;
    }

    const phoneToVerify = phoneNumber;
    setIsVerifyingOTP(true);
    setOtpError('');

    try {
      const data: OTPResponse = await verifyOtp(phoneToVerify, otpString);

      if (data.success && data.verified) {
        toast.success('Phone verified successfully!');
        setOtpVerified(true);
        setOtpError('');
      } else {
        toast.error(data.message || 'Invalid OTP');
        setOtpError(data.message || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpInputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error('OTP verification failed');
      setOtpError('Verification failed. Please try again.');
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleNext = async () => {
    const { firstName, lastName, email, password, confirmPassword, phone, address, city, state, country, pincode } = formData;

    if (!firstName || !lastName || !email || !password || !confirmPassword || !phone || !address || !city || !state || !country || !pincode) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!/^\d{6}$/.test(pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const phoneNumber = phone.replace(/\D/g, '');
    if (!/^\d{10}$/.test(phoneNumber)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    if (!otpVerified || phoneNumber !== phoneNumberForOTP) {
      toast.error('Please verify your phone number first');
      return;
    }

    setCurrentStep(2);
  };

  const validateBankDetails = (): boolean => {
    const errors: typeof bankValidationErrors = {};
    let isValid = true;

    if (formData.bankName.trim()) {
      if (!validateBankName(formData.bankName)) {
        errors.bankName = 'Bank name: Letters, spaces, and hyphens only (3-50 characters)';
        isValid = false;
      }
    }

    if (formData.accountNumber.trim()) {
      if (!validateAccountNumber(formData.accountNumber)) {
        errors.accountNumber = 'Account number must be 9-18 digits with no special characters';
        isValid = false;
      }
    }

    if (formData.ifscCode.trim()) {
      if (!validateIFSCCode(formData.ifscCode)) {
        errors.ifscCode = 'IFSC code format: 4 letters + 0 + 6 alphanumeric (e.g., HDFC0000985)';
        isValid = false;
      }
    }

    setBankValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!otpVerified) {
      toast.error('Please verify your phone number first');
      return;
    }

    if (!formData.agreeToTerms) {
      toast.error('Please agree to Terms & Privacy Policy');
      return;
    }

    if (!validateBankDetails()) {
      toast.error('Please fix bank details errors');
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        dataToSend.append(key, value.toString());
      });

      dataToSend.append('phoneVerified', 'true');
      dataToSend.append('verifiedPhone', phoneNumberForOTP);

      if (certFile) {
        dataToSend.append('certification', certFile);
      }

      const res = await getRegister(dataToSend);

      if (res && (res.message === "Registration Successful!" || res.success)) {
        toast.success("Registration Successful! Please wait for Admin approval.", {
          duration: 6000,
        });

        setTimeout(() => {
          onClose();
          window.location.href = 'https://partner.metafitwellness.com/';
        }, 4000);
      } else {
        toast.error(res?.message || "Registration Failed. Please try again.");
      }
    } catch (err) {
      toast.error("Something went wrong. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;


  const fieldClass = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none";
  const selectFieldClass = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none";

  const stepConfig = [
    { number: 1, label: "Profile" },
    { number: 2, label: "Finish" },
  ];

  const dropdownStyles = `
    @keyframes slideUpFade {
      0% { opacity: 0; transform: translateY(15px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    .animate-stagger-fields > div {
      animation: slideUpFade 0.4s ease-out forwards;
      opacity: 0;
    }
    .animate-stagger-fields > div:nth-child(1) { animation-delay: 0.05s; }
    .animate-stagger-fields > div:nth-child(2) { animation-delay: 0.1s; }
    .animate-stagger-fields > div:nth-child(3) { animation-delay: 0.15s; }
    .animate-stagger-fields > div:nth-child(4) { animation-delay: 0.2s; }
    .animate-stagger-fields > div:nth-child(5) { animation-delay: 0.25s; }
    .animate-stagger-fields > div:nth-child(6) { animation-delay: 0.3s; }
    .animate-stagger-fields > div:nth-child(7) { animation-delay: 0.35s; }
    .animate-stagger-fields > div:nth-child(8) { animation-delay: 0.4s; }
    .animate-stagger-fields > div:nth-child(9) { animation-delay: 0.45s; }
    .animate-stagger-fields > div:nth-child(10) { animation-delay: 0.5s; }
    .animate-stagger-fields > div:nth-child(11) { animation-delay: 0.55s; }
    .animate-stagger-fields > div:nth-child(12) { animation-delay: 0.6s; }
    .animate-stagger-fields > div:nth-child(13) { animation-delay: 0.65s; }

  `;

  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: '1rem',
      borderColor: state.isFocused ? '#3b82f6' : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 4px #dbeafe' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      padding: '2px',
      fontSize: '0.875rem',
      fontWeight: '500',
      backgroundColor: state.isDisabled ? '#f8fafc' : '#ffffff',
      color: state.isDisabled ? '#94a3b8' : '#1e293b',
      WebkitTapHighlightColor: 'transparent',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#cbd5e1'
      }
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize: '0.875rem',
      backgroundColor: state.isSelected ? '#eff6ff' : state.isFocused ? '#f8fafc' : 'white',
      color: state.isSelected ? '#1d4ed8' : '#1e293b',
      cursor: 'pointer'
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '0 8px'
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      backgroundColor: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      marginTop: '8px'
    }),
    menuList: (provided: any) => ({
      ...provided,
      padding: '4px',
      maxHeight: '220px'
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999
    })
  };

  const calculateProgress = () => {
    let filled = 0;
      const requiredFields = [
      otpVerified, // Phone is verified
      formData.firstName,
      formData.lastName,
      formData.email,
      formData.address,
      formData.pincode,
      selectedCountryId > 0,
      selectedStateId > 0,
      formData.city,
      formData.password,
      formData.confirmPassword,
      formData.agreeToTerms // Step 2 requirement
    ];

    requiredFields.forEach(field => {
      if (typeof field === 'boolean') {
        if (field) filled++;
      } else if (typeof field === 'string') {
        if (field.trim().length > 0) filled++;
      } else if (field) {
        filled++;
      }
    });

    return Math.round((filled / requiredFields.length) * 100);
  };

  const progressPercentage = calculateProgress();

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center md:p-6 bg-slate-900/80 backdrop-blur-sm animate-fade-in overflow-y-auto md:overflow-hidden">
      <style>{dropdownStyles}</style>

      {/* Modal Content Card - Two Column Layout */}
      <div className="relative w-full h-full md:h-auto md:max-w-5xl bg-white md:rounded-[32px] shadow-2xl flex flex-col md:flex-row md:max-h-[90vh] md:min-h-[600px] overflow-hidden animate-scale-in border-0 md:border border-slate-200">

        {/* Left Sidebar (Dark) */}
        <div className="hidden md:flex w-[35%] bg-slate-900 p-8 lg:p-10 flex-col justify-between text-white relative overflow-hidden shrink-0">
          {/* Decorative gradient overlay */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>

          <div className="relative z-10">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-xs font-bold tracking-widest mb-8 shadow-lg shadow-blue-600/20">
              <span className="flex h-5 w-5 items-center justify-center rounded bg-white/20 text-[10px]">M</span>
              PHASE {currentStep}/{stepConfig.length}
            </div>

            <h2 className="text-3xl font-extrabold mb-4">{stepConfig[currentStep - 1].label}<br />Information</h2>
            <p className="text-slate-400 text-sm mb-10 leading-relaxed max-w-xs">Tell us about your business and provide your contact details to become a Metafit Partner.</p>

            <h3 className="text-[11px] font-bold text-blue-600 tracking-widest uppercase mb-5">
              Checklist
            </h3>
            <ul className="space-y-4 text-sm text-slate-300 font-medium">
              <li className="flex items-center gap-3">
                <CheckCircle className={`w-[18px] h-[18px] ${formData.firstName ? 'text-blue-600' : 'text-slate-600'}`} />
                <span className={formData.firstName ? 'text-white' : ''}>Your Full Name</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className={`w-[18px] h-[18px] ${formData.email ? 'text-blue-600' : 'text-slate-600'}`} />
                <span className={formData.email ? 'text-white' : ''}>Business Email</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-[18px] h-[18px] text-blue-600" />
                <span className="text-white">Business Type</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className={`w-[18px] h-[18px] ${formData.phone && otpVerified ? 'text-blue-600' : 'text-slate-600'}`} />
                <span className={formData.phone && otpVerified ? 'text-white' : ''}>Contact Phone Number {otpVerified && '(Verified)'}</span>
              </li>
              {currentStep === 2 && (
                <li className="flex items-center gap-3 animate-fade-in">
                  <CheckCircle className={`w-[18px] h-[18px] ${(formData.bankName || formData.accountNumber || formData.ifscCode) ? 'text-blue-600' : 'text-slate-600'}`} />
                  <span className={(formData.bankName || formData.accountNumber || formData.ifscCode) ? 'text-white' : ''}>Bank Details</span>
                </li>
              )}
            </ul>
          </div>

          {/* Progress Bar */}
          <div className="relative z-10 mt-12">
            <a
              href="https://youtu.be/o2iy8Hrls6w"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors mb-4"
            >
              <Video className="w-4 h-4 text-red-500" />
              Need help registering? Click here!
            </a>
            <div className="flex justify-between text-[10px] font-bold mb-3 tracking-widest uppercase">
              <span className="text-slate-400">Progress</span>
              <span className="text-blue-600">{progressPercentage}%</span>
            </div>
            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-600 to-teal-500 transition-all duration-700 ease-out" style={{ width: `${progressPercentage}%` }}></div>
            </div>
          </div>
        </div>

        {/* Right Content (Light) */}
        <div className="w-full md:w-[65%] flex-1 flex flex-col relative bg-slate-50/30 overflow-hidden">
          {/* Mobile Header */}
          <div className="md:hidden flex items-center justify-between p-5 border-b border-slate-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 shadow-md">
                <span className="text-sm font-bold text-white">M</span>
              </div>
              <div>
                <p className="text-[10px] font-bold text-blue-600 tracking-widest uppercase mb-0.5">PHASE {currentStep}/{stepConfig.length}</p>
                <h2 className="text-sm font-extrabold text-slate-900">{stepConfig[currentStep - 1].label}</h2>
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 bg-slate-100 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Mobile YouTube Tutorial link */}
          <div className="md:hidden px-5 py-2.5 bg-slate-50 border-b border-slate-200 flex justify-between items-center text-xs">
            <span className="text-slate-500 font-medium">Need help registering?</span>
            <a
              href="https://youtu.be/o2iy8Hrls6w"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 font-bold text-blue-600 hover:text-blue-700 transition-colors"
            >
              <Video className="w-4 h-4 text-red-500" />
              Watch Tutorial
            </a>
          </div>

          {/* Mobile Progress Bar */}
          <div className="md:hidden w-full h-1 bg-slate-100 relative z-10">
            <div className="h-full bg-gradient-to-r from-blue-600 to-teal-500 transition-all duration-700 ease-out" style={{ width: `${progressPercentage}%` }}></div>
          </div>

          {/* Desktop Close Button */}
          <button type="button" onClick={onClose} className="hidden md:flex absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-full z-10 transition-colors">
            <X size={24} />
          </button>

          <div className="flex-1 overflow-y-auto p-6 md:p-12 custom-scrollbar relative">
            <h3 className="hidden md:flex text-[11px] font-bold text-blue-600 tracking-widest uppercase mb-8 items-center gap-4">
              Registration Phase <div className="h-px bg-blue-200 flex-1"></div>
            </h3>

            <form className="space-y-6">
              {currentStep === 1 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-stagger-fields">
                  {otpVerified && (
                    <div className="md:col-span-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                      <div className="flex items-center gap-2 text-green-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm font-bold">Phone number verified ✓</span>
                      </div>
                    </div>
                  )}

                  {/* Phone Number & Verification */}
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-bold text-slate-700">
                      Phone Number *
                      {otpVerified && (
                        <span className="ml-2 text-xs text-green-600 font-normal">(Verified ✓)</span>
                      )}
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <input
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`${fieldClass} ${otpVerified ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : ''}`}
                          placeholder="10-digit number"
                          maxLength={10}
                          disabled={otpVerified}
                        />
                      </div>
                      {!otpVerified && (
                        <button
                          type="button"
                          onClick={sendOTP}
                          disabled={isSendingOTP || !formData.phone || !/^\d{10}$/.test(formData.phone.replace(/\D/g, '')) || resendTimer > 0}
                          className="rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap h-[44px] flex items-center justify-center"
                        >
                          {isSendingOTP ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : otpSent ? 'Resend OTP' : 'Send OTP'}
                        </button>
                      )}
                    </div>

                    {otpSent && !otpVerified && (
                      <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/30 p-4">
                        <label className="mb-2 block text-sm font-bold text-slate-700">Enter OTP</label>
                        <div className="flex gap-1.5 sm:gap-2 mb-3">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              ref={(el) => { otpInputRefs.current[index] = el; }}
                              type="text"
                              inputMode="numeric"
                              pattern="\d"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              className="h-10 w-9 sm:h-14 sm:w-12 rounded-xl sm:rounded-2xl border-2 border-slate-200 bg-white text-center text-base sm:text-xl font-bold text-slate-800 shadow-sm outline-none transition focus:border-blue-600 focus:ring-4 focus:ring-blue-100 disabled:opacity-50"
                              disabled={isVerifyingOTP || otpVerified}
                            />
                          ))}
                        </div>
                        {otpError && (
                          <p className="text-red-500 text-sm font-medium mb-2">{otpError}</p>
                        )}
                        <button
                          type="button"
                          onClick={() => verifyOTP()}
                          disabled={otp.some(digit => digit === '') || isVerifyingOTP}
                          className="w-full sm:w-auto rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center"
                        >
                          {isVerifyingOTP ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Verifying...
                            </>
                          ) : 'Verify OTP'}
                        </button>
                      </div>
                    )}
                    {!otpSent && !otpVerified && (
                      <p className="mt-1.5 text-xs text-slate-500">We'll send OTP via WhatsApp</p>
                    )}
                  </div>

                  {/* First Name */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">First Name *</label>
                    <input
                      name="firstName"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={fieldClass}
                      placeholder="First name"
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">Last Name *</label>
                    <input
                      name="lastName"
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={fieldClass}
                      placeholder="Last name"
                    />
                  </div>

                  {/* Email Address */}
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-bold text-slate-700">Email Address *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className={fieldClass}
                      placeholder="Enter your email"
                    />
                  </div>

                  {/* Address */}
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-bold text-slate-700">Address *</label>
                    <textarea
                      name="address"
                      required
                      rows={2}
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none"
                      placeholder="Enter your full address"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">Country *</label>
                    <ReactSelect
                      options={countries}
                      isLoading={isLoadingCountries}
                      placeholder="Select country"
                      styles={customSelectStyles}
                      onChange={(option: any) => {
                        setSelectedCountryId(option ? option.value : 0);
                        setSelectedStateId(0);
                        setFormData(prev => ({ ...prev, country: option ? option.name : '', state: '', city: '' }));
                      }}
                      value={countries.find(c => c.value === selectedCountryId) || null}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  {/* Pincode */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">Pincode *</label>
                    <input
                      name="pincode"
                      type="text"
                      inputMode="numeric"
                      required
                      value={formData.pincode}
                      onChange={handleInputChange}
                      className={fieldClass}
                      placeholder="6-digit pincode"
                      maxLength={6}
                    />
                  </div>

                  {/* State */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">State *</label>
                    <ReactSelect
                      options={states}
                      isLoading={isLoadingStates}
                      isDisabled={!selectedCountryId}
                      placeholder={selectedCountryId ? "Select state" : "Select country first"}
                      styles={customSelectStyles}
                      onChange={(option: any) => {
                        setSelectedStateId(option ? option.value : 0);
                        setFormData(prev => ({ ...prev, state: option ? option.name : '', city: '' }));
                      }}
                      value={states.find(s => s.value === selectedStateId) || null}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">City *</label>
                    <ReactSelect
                      options={cities}
                      isLoading={isLoadingCities}
                      isDisabled={!selectedCountryId || !selectedStateId}
                      placeholder={selectedStateId ? "Select city" : "Select state first"}
                      styles={customSelectStyles}
                      onChange={(option: any) => {
                        setFormData(prev => ({ ...prev, city: option ? option.name : '' }));
                      }}
                      value={cities.find(c => c.name === formData.city) || null}
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">Password *</label>
                    <div className="relative">
                      <input
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={formData.password}
                        onChange={handleInputChange}
                        className={fieldClass}
                        placeholder="Create password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">Confirm Password *</label>
                    <div className="relative">
                      <input
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={fieldClass}
                        placeholder="Confirm password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-650 z-10"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="md:col-span-2 pt-4">
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={
                        isSendingOTP ||
                        !formData.phone ||
                        !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))
                      }
                      className="w-full rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-3 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Continue to Bank Details
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5 animate-stagger-fields">
                  {otpVerified && (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" style={{ animation: 'slideUpFade 0.4s ease-out forwards' }}>
                      <div className="flex items-center gap-2 text-green-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="font-bold text-sm">Phone Verified Successfully! ✓</span>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Bank Name */}
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-bold text-slate-700">Bank Name (Optional)</label>
                      <input
                        name="bankName"
                        type="text"
                        placeholder="e.g., HDFC Bank"
                        value={formData.bankName}
                        onChange={handleInputChange}
                        maxLength={50}
                        className={`w-full rounded-2xl border px-4 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition ${bankValidationErrors.bankName
                          ? 'border-red-500 focus:ring-4 focus:ring-red-100'
                          : 'border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                          }`}
                      />
                      {bankValidationErrors.bankName && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1 font-semibold">
                          <span>⚠</span> {bankValidationErrors.bankName}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-500 mt-1">
                        Letters, spaces, and hyphens only • 3-50 characters
                      </p>
                    </div>

                    {/* Account Number */}
                    <div>
                      <label className="mb-2 block text-sm font-bold text-slate-700">Account Number (Optional)</label>
                      <input
                        name="accountNumber"
                        type="text"
                        placeholder="9-18 digits"
                        value={formData.accountNumber}
                        onChange={handleInputChange}
                        maxLength={18}
                        inputMode="numeric"
                        className={`w-full rounded-2xl border px-4 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition ${bankValidationErrors.accountNumber
                          ? 'border-red-500 focus:ring-4 focus:ring-red-100'
                          : 'border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                          }`}
                      />
                      {bankValidationErrors.accountNumber && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1 font-semibold">
                          <span>⚠</span> {bankValidationErrors.accountNumber}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-500 mt-1">
                        9-18 digits only, no spaces or special characters
                      </p>
                    </div>

                    {/* IFSC Code */}
                    <div>
                      <label className="mb-2 block text-sm font-bold text-slate-700">IFSC Code (Optional)</label>
                      <input
                        name="ifscCode"
                        type="text"
                        placeholder="e.g., HDFC0000985"
                        value={formData.ifscCode}
                        onChange={handleInputChange}
                        maxLength={11}
                        className={`w-full rounded-2xl border px-4 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition ${bankValidationErrors.ifscCode
                          ? 'border-red-500 focus:ring-4 focus:ring-red-100'
                          : 'border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                          }`}
                      />
                      {bankValidationErrors.ifscCode && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1 font-semibold">
                          <span>⚠</span> {bankValidationErrors.ifscCode}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-500 mt-1">
                        Format: 4 letters + 0 + 6 alphanumeric
                      </p>
                    </div>
                  </div>

                  {/* Upload Certification */}
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">Upload Certification (PDF/Image)</label>
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-5 flex flex-col items-center justify-center transition hover:bg-blue-50/50">
                      <Upload size={24} className="text-slate-400 mb-2" />
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={handleCertChange}
                        className="text-xs text-slate-550 w-full text-center"
                      />
                      {certFile && <p className="text-xs text-green-600 font-bold mt-2">Selected: {certFile.name}</p>}
                    </div>
                  </div>

                  {/* Terms & Privacy */}
                  <div className="flex items-start">
                    <input
                      id="agree"
                      name="agreeToTerms"
                      type="checkbox"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      className="mt-1 h-4 w-4 text-blue-700 border-gray-300 rounded focus:ring-blue-600"
                    />
                    <label htmlFor="agree" className="ml-2 text-sm text-slate-655 font-medium">
                      I agree to the <span className="text-blue-700 underline font-semibold cursor-pointer">Terms & Privacy Policy</span>
                    </label>
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 rounded-2xl border border-slate-300 py-3 font-bold text-slate-700 transition hover:bg-slate-50"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      disabled={!formData.agreeToTerms || isSubmitting || !otpVerified}
                      onClick={handleSubmit}
                      className="flex-1 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 py-3 font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50"
                    >
                      {isSubmitting ? "Creating Account..." : "Complete Registration"}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RegisterModal;
